import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  Alert,
  CircularProgress,
  MenuItem,
} from "@mui/material";

import { collection, addDoc, query, orderBy, getDocs } from "firebase/firestore";
import { db } from "../firebase";


const AddMainServiceForm = ({ onServiceAdded }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    imageUrl: "",
    priority: 0,
    ctaUrl: "",
    slugId: "", 
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "priority" ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    // Use the slugId field name internally to match the data structure
    const serviceSlug = formData.slugId.toLowerCase().replace(/\s+/g, '-');

    if (!serviceSlug || !formData.title) {
      setMessage({ type: "error", text: "ID (Slug) and Title are required." });
      setLoading(false);
      return;
    }

    try {
      // Use addDoc() to let Firestore auto-generate the document ID.
      // We store the user-friendly slug in a field 'slugId' for routing.
      await addDoc(collection(db, "services"), {
        slugId: serviceSlug, 
        title: formData.title,
        description: formData.description,
        imageUrl: formData.imageUrl,
        priority: formData.priority,
        ctaUrl: formData.ctaUrl,
      });

      setMessage({ type: "success", text: `Service "${formData.title}" added successfully! (ID: Auto-generated)` });
      
      // Reset form and trigger the parent component's refresh function
      setFormData({ title: "", description: "", imageUrl: "", priority: 0, ctaUrl: "", slugId: "" }); 
      onServiceAdded(); 

    } catch (error) {
      console.error("Error adding main service: ", error);
      setMessage({ type: "error", text: `Error: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ p: { xs: 2.5, md: 4 }, mb: 4, height: "100%" }}>
      <Typography variant="h5" mb={3} fontFamily="Georgia, 'Times New Roman', serif">
        Add New Main Service
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="ID Service Id (Slug for URL)"
              name="slugId"
              value={formData.slugId}
              onChange={handleChange}
              placeholder="e.g., web-design"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Short Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={2}
              placeholder="Short Description"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Image URL (imageUrl)"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Priority"
              name="priority"
              type="number"
              value={formData.priority}
              onChange={handleChange}
              inputProps={{ min: 0 }}
            />
            <Typography variant="caption" color="text.secondary">
              Lower number appears first.
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="CTA URL"
              name="ctaUrl"
              value={formData.ctaUrl}
              onChange={handleChange}
              placeholder="min 0"
            />
          </Grid>
          <Grid item xs={12} mt={2}>
            <Button
              type="submit"
              variant="contained"
              sx={{ bgcolor: "#2B211C",
                 "&:hover": { bgcolor: "#333" } }}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              Save Service
            </Button>
            {message && (
              <Alert 
                severity={message.type} 
                sx={{ mt: 2 }}
              >
                {message.text}
              </Alert>
            )}
          </Grid>
        </Grid>
      </form>
    </Card>
  );
};

// 2. Add New Sub-Service Form
// Props: refreshKey - dependency to trigger refetch of main services
const AddSubServiceForm = ({ refreshKey }) => {
  const [mainServices, setMainServices] = useState([]);
  const [formData, setFormData] = useState({
    mainServiceId: "",
    title: "",
    description: "",
    imageUrl: "",
    priority: 0,
    ctaUrl: "",
    slugId: "", 
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);

  // Fetch list of main services for the Select dropdown
  // NOTE: This now depends on refreshKey, so it re-runs when a new main service is added.
  const fetchMainServices = useCallback(async () => {
    setFetchLoading(true);
    try {
      const colRef = collection(db, "services");
      const q = query(colRef, orderBy("title", "asc"));
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ 
        // We need the document's auto-generated ID to construct the subcollection path.
        id: d.id, 
        slugId: d.data().slugId || d.id,
        title: d.data().title 
      }));
      setMainServices(data);
    } catch (err) {
      console.error("Error fetching main services:", err);
    } finally {
      setFetchLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMainServices();
  }, [fetchMainServices, refreshKey]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "priority" ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    if (!formData.mainServiceId || !formData.title) {
      setMessage({ type: "error", text: "Main Service ID and Title are required." });
      setLoading(false);
      return;
    }
    
    // Path: services/{mainServiceId}/subservices
    // formData.mainServiceId holds the auto-generated Firestore ID of the parent service.
    const subCollectionPath = `services/${formData.mainServiceId}/subservices`;

    try {
        await addDoc(collection(db, subCollectionPath), {
            title: formData.title,
            description: formData.description,
            imageUrl: formData.imageUrl,
            priority: formData.priority,
            ctaUrl: formData.ctaUrl,
            slugId: formData.slugId ? formData.slugId.toLowerCase().replace(/\s+/g, '-') : undefined,
        });

      setMessage({ type: "success", text: `Sub-Service "${formData.title}" added successfully!` });
      setFormData((prev) => ({ 
          mainServiceId: prev.mainServiceId, 
          title: "", 
          description: "", 
          imageUrl: "", 
          priority: 0, 
          ctaUrl: "", 
          slugId: ""
      }));
    } catch (error) {
      console.error("Error adding sub-service: ", error);
      setMessage({ type: "error", text: `Error: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ p: { xs: 2.5, md: 4 }, mb: 4, height: "100%" }}>
      <Typography variant="h5" mb={3} fontFamily="Georgia, 'Times New Roman', serif">
        Add New Sub-Service
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Select Main ServiceId"
              name="mainServiceId"
              value={formData.mainServiceId}
              onChange={handleChange}
              required
              disabled={fetchLoading}
            >
              {fetchLoading ? (
                <MenuItem disabled value="">
                  <CircularProgress size={20} /> Loading Services...
                </MenuItem>
              ) : (
                mainServices.map((service) => (
                  <MenuItem key={service.id} value={service.id}>
                    {service.title} (ID: {service.id})
                  </MenuItem>
                ))
              )}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Short Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={2}
              placeholder="description"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Image URL (imageUrl)"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Priority"
              name="priority"
              type="number"
              value={formData.priority}
              onChange={handleChange}
              inputProps={{ min: 0 }}
            />
            <Typography variant="caption" color="text.secondary">
              Lower number appears first.
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="CTA URL"
              name="ctaUrl"
              value={formData.ctaUrl}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
             <TextField
              fullWidth
              label="Sub-Service ID (Optional Slug)"
              name="slugId"
              value={formData.slugId}
              onChange={handleChange}
              placeholder="e.g., frontend-design"
            />
          </Grid>
          <Grid item xs={12} mt={2}>
            <Button
              type="submit"
              variant="contained"
              sx={{ bgcolor: "#2B211C", "&:hover": { bgcolor: "#333" } }}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              Add Sub-Service
            </Button>
            {message && (
              <Alert 
                severity={message.type} 
                sx={{ mt: 2 }}
              >
                {message.text}
              </Alert>
            )}
          </Grid>
        </Grid>
      </form>
    </Card>
  );
};


// 3. Main Page Component
const ServiceAdminPage = () => {
    // This state is used to force the SubService form to re-fetch main services.
    const [refreshKey, setRefreshKey] = useState(0);

    // Function passed to the Main Service form to trigger a refresh
    const handleServiceAdded = () => {
        setRefreshKey(prev => prev + 1);
    };

    return (
        <Container
              maxWidth="xl"
              sx={{
                pt: { xs: 10, md: 14 },
                pb: 10,
                minHeight: "100vh",
                color: "#2B211C",
                "& .MuiCard-root": {
                  borderRadius: "2px",
                  border: "1px solid #DCD4CC",
                  backgroundColor: "#FCFAF6",
                  boxShadow: "0 20px 60px rgba(43,33,28,0.08)",
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "13px",
                  backgroundColor: "#FFFDF8",
                },
              }}
            >
            <Typography variant="h3" mb={5} fontFamily="Georgia, 'Times New Roman', serif" fontWeight="bold">
                Service Administration Dashboard
            </Typography>
            
            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <AddMainServiceForm onServiceAdded={handleServiceAdded} />
                </Grid>
                <Grid item xs={12} md={6}>
                    {/* SubService form uses the refreshKey to re-fetch the list */}
                    <AddSubServiceForm refreshKey={refreshKey} />
                </Grid>
            </Grid>
        </Container>
    );
};

export default ServiceAdminPage;